import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Alert, Badge, Button, Col, Container, FormGroup, Row, Spinner } from 'reactstrap';
import { VocabularyService } from '../../../services';
import useDebounce from '../../../utils/useDebounce';
import fileDownload from 'js-file-download';
import Pagination from './Pagination';
import moment from 'moment';

const SIZE = 10;

const synth = window.speechSynthesis;
const speak = (text) => {
  const uttr = new SpeechSynthesisUtterance();
  uttr.text = text;
  uttr.rate = 0.8;
  synth.speak(uttr);
};

const PageVocabularies = () => {
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);

  const debouncedKeyword = useDebounce(keyword.trim(), 500);
  const variables = { keyword: debouncedKeyword, page, size: SIZE };

  const { data, isLoading, refetch } = useQuery(
    ['getVocabularies', variables],
    () => VocabularyService.getVocabularies(variables),
    { retry: false },
  );

  const items = data?.data || [];
  const count = data?.vocabularyCount || 0;
  const totalPage = Math.ceil(count / SIZE);

  const { mutate: exportCsv } = useMutation(VocabularyService.exportVocabularies, {
    onSuccess: (data) => {
      fileDownload(data, `Vocabularies_${moment().format('YYYY_MM_DD')}.csv`);
    },
  });

  const { mutate: deleteWord } = useMutation(VocabularyService.deleteVocabulary, {
    onSuccess: () => refetch(),
  });

  const { mutate: toggleMemorized } = useMutation(VocabularyService.updateVocabulary, {
    onSuccess: () => refetch(),
  });

  return (
    <section className="section p-0 mt-4">
      <Container>
        <Row className="align-items-center mb-3">
          <Col md={7}>
            <FormGroup className="mb-0">
              <input
                type="text"
                className="form-control rounded-pill shadow-sm"
                placeholder="Search your words..."
                value={keyword}
                onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
              />
            </FormGroup>
          </Col>
          <Col md={5} className="text-md-end mt-2 mt-md-0">
            <Button color="outline-primary" size="sm" className="rounded-pill" onClick={exportCsv}>
              <i className="uil uil-file-download me-1" />
              Export CSV
            </Button>
          </Col>
        </Row>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2">Loading your words...</p>
          </div>
        ) : items.length === 0 ? (
          <Alert color="info">
            {keyword ? `No words match "${keyword}".` : "You haven't saved any words yet. Use the extension to add words while you browse."}
          </Alert>
        ) : (
          <>
            <div className="bg-white rounded shadow-sm">
              {items.map((item, i) => (
                <div
                  key={item.id}
                  className="d-flex align-items-start gap-3 p-3"
                  style={{ borderBottom: i < items.length - 1 ? '1px solid #f1f3f5' : 'none' }}
                >
                  <div className="flex-grow-1 min-width-0">
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                      <span className="fw-bold text-dark">
                        {String(item.vocabulary).charAt(0).toUpperCase() + String(item.vocabulary).slice(1)}
                      </span>
                      {item.pronunciation && (
                        <>
                          <button
                            className="btn btn-link p-0 text-muted"
                            style={{ fontSize: '1rem', lineHeight: 1 }}
                            onClick={() => speak(item.vocabulary)}
                            title="Listen"
                          >
                            <i className="uil uil-volume-up" />
                          </button>
                          <span className="text-muted small">{item.pronunciation}</span>
                        </>
                      )}
                      {item.translation && (
                        <Badge color="light" className="text-primary border fw-normal">
                          {item.translation}
                        </Badge>
                      )}
                      {item.memorized && (
                        <Badge color="success" pill className="fw-normal">Memorized</Badge>
                      )}
                    </div>
                    {item.definition && (
                      <p className="mb-0 text-dark small">{item.definition}</p>
                    )}
                    {item.example && (
                      <p className="mb-0 text-muted small fst-italic">"{item.example}"</p>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      color={item.memorized ? 'success' : 'outline-secondary'}
                      className="rounded-pill"
                      style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                      onClick={() => toggleMemorized({ id: item.id, memorized: !item.memorized })}
                      title={item.memorized ? 'Mark as learning' : 'Mark as memorized'}
                    >
                      {item.memorized ? '✓ Memorized' : 'Mark Memorized'}
                    </Button>
                    <Button
                      size="sm"
                      color="outline-danger"
                      className="rounded-pill"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => deleteWord(item.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {totalPage >= 1 && (
              <div className="mt-3">
                <Pagination currentPage={page} cb={setPage} totalPage={totalPage} />
              </div>
            )}
          </>
        )}
      </Container>
    </section>
  );
};

export default PageVocabularies;
